import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Loader2,
  User,
  CheckCircle,
  Download,
  ArrowRight,
  Shield,
  Zap,
  Sparkles,
  Users,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NameSplitterPage = () => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const processFile = async () => {
    if (!file) return;
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/tools/name-splitter/process`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      );
      
      setResult(response.data);
      
      if (response.data.total_rows > 100) {
        toast.info(`Split first 100 rows. ${response.data.total_rows - 100} rows remaining - sign up to split the rest.`);
      } else {
        toast.success('All names split successfully!');
      }
      
    } catch (error) {
      console.error('Processing error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process file';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetTool = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 sm:pb-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4">
            <Users className="w-4 h-4" />
            Free Name Splitter
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            Full Name Splitter
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Split full names into First Name and Last Name columns. 
            Perfect for HubSpot imports that require separate name fields.
          </p>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Zap className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Smart Parsing</p>
            <p className="text-sm text-slate-500">Handles complex names</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">First 100 Free</p>
            <p className="text-sm text-slate-500">No signup required</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <User className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Title Case</p>
            <p className="text-sm text-slate-500">Proper capitalization</p>
          </div>
        </div>

        {!result ? (
          /* Upload Section */
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${file ? 'border-purple-400 bg-purple-50' : 'border-slate-300 hover:border-purple-400 hover:bg-slate-50'}`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="name-splitter-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="name-splitter-file-input"
              />
              
              {file ? (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto">
                    <FileSpreadsheet className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); resetTool(); }}
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                    <Upload className="w-7 h-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Drop your CRM export here</p>
                    <p className="text-sm text-slate-500">CSV or Excel file (max 50MB)</p>
                  </div>
                </div>
              )}
            </div>

            {/* Process Button */}
            {file && (
              <Button
                onClick={processFile}
                disabled={processing}
                className="w-full mt-6 h-14 text-lg bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="name-splitter-process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Splitting Names...
                  </>
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Split Names (Free)
                  </>
                )}
              </Button>
            )}

            {/* How it works */}
            <div className="mt-8 pt-6 border-t border-slate-100">
              <h3 className="font-semibold text-slate-900 mb-4">What this tool does:</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Smart parsing:</strong> "John Smith" → First: John, Last: Smith</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Middle names:</strong> Included with first name or separated</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Suffixes:</strong> Handles Jr., Sr., III, etc.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Title case:</strong> Properly capitalizes names</span>
                </li>
              </ul>
              
              {/* Examples */}
              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-700 mb-3">Examples:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">"john doe"</span>
                    <span className="text-purple-600">→ John | Doe</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">"MARY ANN JOHNSON"</span>
                    <span className="text-purple-600">→ Mary Ann | Johnson</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">"Dr. James Smith Jr."</span>
                    <span className="text-purple-600">→ James | Smith</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Names Split!</h2>
                  <p className="text-slate-500">{result.file_name}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{result.total_rows}</p>
                  <p className="text-sm text-slate-500">Total Rows</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{result.split_count}</p>
                  <p className="text-sm text-slate-500">Names Split</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-400">{result.empty_count}</p>
                  <p className="text-sm text-slate-500">Empty</p>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <p className="font-medium text-slate-700">Preview (first 5 rows)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Row</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Original Name</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">First Name</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Last Name</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.preview?.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-500">#{row.row}</td>
                          <td className="px-4 py-3 text-slate-600">{row.original || '-'}</td>
                          <td className="px-4 py-3 text-purple-600 font-medium">{row.first_name || '-'}</td>
                          <td className="px-4 py-3 text-purple-600 font-medium">{row.last_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Button - With Email Capture for Anonymous Users */}
              <Button 
                onClick={() => {
                  if (user?.email) {
                    window.open(`${API_URL}${result.download_url}`, '_blank');
                    toast.success('Download started!');
                  } else {
                    setShowEmailModal(true);
                  }
                }}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Split Names File
              </Button>
            </div>

            {/* Freemium Upsell */}
            {result.total_rows > 100 && (
              <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 sm:p-8 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">
                      We split the first 100 rows for free!
                    </h3>
                    <p className="text-indigo-100 mb-4">
                      To split the remaining <span className="font-bold text-white">{result.total_rows - 100} rows</span> and 
                      access 50+ other cleaning rules, sign up for free.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/login">
                        <Button className="bg-white text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto">
                          Sign Up Free
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* All rows processed */}
            {result.total_rows <= 100 && (
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 text-center">
                <CheckCircle className="w-12 h-12 text-purple-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-purple-800 mb-2">
                  All {result.total_rows} names split successfully!
                </h3>
                <p className="text-purple-700 mb-4">
                  Need more cleaning? Try our full data cleaner with 50+ rules.
                </p>
                <Link to="/login">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    Try Full Data Cleaner (Free)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {/* Process Another */}
            <div className="text-center">
              <Button variant="ghost" onClick={resetTool}>
                Process Another File
              </Button>
            </div>
          </div>
        )}

        {/* Trust Badges */}
        {!result && (
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span>Secure processing</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              <span>No signup required</span>
            </div>
          </div>
        )}
      </main>
      
      {/* Email Capture Modal for Anonymous Users */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={(downloadUrl) => {
          window.open(`${API_URL}${result.download_url}`, '_blank');
          toast.success('Download started!');
          setShowEmailModal(false);
        }}
        tool="name-splitter"
        fileId={result?.file_id || result?.download_url?.split('/').pop()}
        toolName="Name Splitter"
        rowsProcessed={result?.total_rows}
        accentColor="purple"
      />
      
      <SupportButton />
    </div>
  );
};

export default NameSplitterPage;
